import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface QuotaSettings {
  maxQuotesPerDay: number;
  maxSubscribers: number;
  rateLimitPerMinute: number;
}

export function QuotaSettings() {
  const [settings, setSettings] = useState<QuotaSettings>({
    maxQuotesPerDay: 24,
    maxSubscribers: 1000,
    rateLimitPerMinute: 60
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('admin_settings')
          .select('setting_key, setting_value')
          .in('setting_key', [
            'max_quotes_per_day',
            'max_subscribers',
            'rate_limit_per_minute'
          ]);

        if (error) throw error;

        const settingsMap = data?.reduce((acc, item) => {
          acc[item.setting_key] = item.setting_value;
          return acc;
        }, {} as Record<string, any>);

        setSettings({
          maxQuotesPerDay: settingsMap?.max_quotes_per_day || 24,
          maxSubscribers: settingsMap?.max_subscribers || 1000,
          rateLimitPerMinute: settingsMap?.rate_limit_per_minute || 60
        });
      } catch (error) {
        console.error("Error fetching quota settings:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load quota settings"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updates = [
        {
          setting_key: 'max_quotes_per_day',
          setting_value: settings.maxQuotesPerDay,
          description: 'Maximum quotes per day per subscriber'
        },
        {
          setting_key: 'max_subscribers',
          setting_value: settings.maxSubscribers,
          description: 'Maximum number of subscribers allowed'
        },
        {
          setting_key: 'rate_limit_per_minute',
          setting_value: settings.rateLimitPerMinute,
          description: 'Maximum quote deliveries per minute'
        }
      ];

      const { error } = await supabase
        .from('admin_settings')
        .upsert(updates, { onConflict: 'setting_key' });

      if (error) throw error;

      toast({
        title: "Settings Updated",
        description: "Quota settings have been updated successfully"
      });
    } catch (error) {
      console.error("Error saving quota settings:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save quota settings"
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>System Quotas</CardTitle>
          <CardDescription>
            Configure system-wide limits and quotas
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Maximum Quotes per Day</Label>
            <Input
              type="number"
              min="1"
              max="100"
              value={settings.maxQuotesPerDay}
              onChange={(e) => 
                setSettings(s => ({
                  ...s,
                  maxQuotesPerDay: parseInt(e.target.value) || 24
                }))
              }
            />
            <p className="text-sm text-muted-foreground">
              Maximum number of quotes a subscriber can receive per day
            </p>
          </div>

          <div className="space-y-2">
            <Label>Maximum Subscribers</Label>
            <Input
              type="number"
              min="1"
              value={settings.maxSubscribers}
              onChange={(e) => 
                setSettings(s => ({
                  ...s,
                  maxSubscribers: parseInt(e.target.value) || 1000
                }))
              }
            />
            <p className="text-sm text-muted-foreground">
              Maximum number of subscribers allowed in the system
            </p>
          </div>

          <div className="space-y-2">
            <Label>Rate Limit (per minute)</Label>
            <Input
              type="number"
              min="1"
              max="1000"
              value={settings.rateLimitPerMinute}
              onChange={(e) => 
                setSettings(s => ({
                  ...s,
                  rateLimitPerMinute: parseInt(e.target.value) || 60
                }))
              }
            />
            <p className="text-sm text-muted-foreground">
              Maximum number of quotes that can be delivered per minute
            </p>
          </div>

          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
} 